import styled from '@emotion/styled'
import { byNameOf } from '@pubstack/common/src/sort'
import { StacksBySite } from '@pubstack/common/src/stack'
import { ReactNode } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Tooltip } from '~/components/Tooltip'

const StacksTooltip = styled.div`
  div:not(:last-of-type) {
    border-bottom: 1px solid ${Colors.SlateGrey};
    padding-bottom: 6px;
  }
  div:not(:first-of-type) {
    padding-top: 6px;
  }
`

const ArchivedStackName = styled.span`
  ${Fonts.colors.Silver};
  font-style: italic;
`

/**
 * One media's stacks, by name: the live ones, then the archived ones after them.
 *
 * Read in this order, so join it front to back — `reduce((acc, stack) => [acc, separator, stack])`.
 */
export const generateStackNameList = (stacks: StacksBySite[string]): ReactNode[] => {
  const byStackName = [...stacks].sort(byNameOf((stack) => stack.stackName))
  return [
    ...byStackName
      .filter((s) => s.enabled)
      .map((stack) => (
        <span key={stack.stackName + stack.version}>
          {stack.stackName} - {stack.version}
        </span>
      )),
    ...byStackName
      .filter((s) => !s.enabled)
      .map((stack) => (
        <ArchivedStackName key={stack.stackName + stack.version}>
          {stack.stackName} - {stack.version}
        </ArchivedStackName>
      )),
  ]
}

type PureUsedStackTooltipProps = { stacksBySite: StacksBySite }
const UnstyledPureUsedStackTooltip = ({ stacksBySite }: PureUsedStackTooltipProps) => {
  const pipeSeparator = `\xa0 | \xa0`
  const countStacksUse = stacksBySite
    ? Object.entries(stacksBySite)
        .map(([_, stacks]) => stacks.length)
        .reduce((sum, current) => sum + current, 0)
    : 0

  return (
    <Tooltip
      title={
        <StacksTooltip>
          {Object.entries(stacksBySite)
            .sort(byNameOf(([siteName]) => siteName))
            .map(([siteName, stacks]) => (
              <div key={siteName}>
                <strong>{siteName}: </strong>
                {generateStackNameList(stacks).reduce((acc, stack) => [acc, pipeSeparator, stack])}
              </div>
            ))}
        </StacksTooltip>
      }
      positions={['left', 'bottom', 'top', 'right']}
    >
      {`${countStacksUse} stack${countStacksUse > 1 ? 's' : ''}`}
    </Tooltip>
  )
}

export const PureUsedStackTooltip = styled(UnstyledPureUsedStackTooltip)``
